import { Routes, Route, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { chooseAuthScreen } from './lib/authScreen.js';
import AppShell from './components/layout/AppShell.jsx';
import Organizations from './pages/Organizations.jsx';
import OrgDetail from './pages/OrgDetail.jsx';
import PlatformUsers from './pages/PlatformUsers.jsx';
import PlatformHealth from './pages/PlatformHealth.jsx';
import Releases from './pages/Releases.jsx';

// #790: nested catch-all so unknown admin paths show a 404 inside the shell,
// not a blank outlet.
function NotFound() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <p className="text-5xl font-bold text-gray-300">404</p>
      <h1 className="mt-3 text-lg font-semibold text-gray-900">Page not found</h1>
      <p className="mt-1 text-sm text-gray-500">
        The page you&apos;re looking for doesn&apos;t exist or has moved.
      </p>
      <Link
        to="/"
        className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Back to Organizations
      </Link>
    </div>
  );
}

function AuthGate({ children }) {
  const { loading, isAuthenticated, error, errorType, refetch } = useAuth();

  // vista#671: the branching lives in a pure, unit-tested function, the same
  // way the org dashboard extracted `buildAuthValue` — this console has no DOM
  // test harness, so a pure decision is the difference between the branching
  // being covered and not.
  switch (chooseAuthScreen({ loading, isAuthenticated, errorType })) {
    case 'loading':
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            <p className="mt-4 text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      );

    // Not signed in. This replaces
    // `setTimeout(() => location.href = '/cdn-cgi/access/login', 2000)`, which
    // was meant to bounce the browser through CF Access — and does, when the
    // domain is fronted by an Access application policy. When it is not, it
    // lands back here, 401s again, and loops, leaving a flashing
    // "redirecting…" panel with no way out. The org console removed the same
    // pattern in vista#372 for the same reason; this one kept it. The link
    // goes to the same place, on a click, so a loop is impossible.
    case 'sign_in':
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Sign in required</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your session could not be established. Sign in with Cloudflare
              Access to continue.
            </p>
            <a
              href="/cdn-cgi/access/login"
              className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Sign In
            </a>
          </div>
        </div>
      );

    // A session that aged out, or an API we could not reach. The user was fine
    // a moment ago, so Retry is the useful affordance and Sign In would be
    // misleading.
    case 'retry':
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Unable to Connect</h1>
            <p className="mt-2 text-sm text-gray-600">
              {error || 'Could not reach the VISTA Admin API. Please try again.'}
            </p>
            <button
              onClick={refetch}
              className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      );

    // Signed in, and still refused. No affordance, because only an operator
    // can change it.
    case 'denied':
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Access Denied</h1>
            <p className="mt-2 text-sm text-gray-600">
              {error || 'Access restricted to Colorado Communications administrators.'}
            </p>
          </div>
        </div>
      );

    default:
      return children;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/" element={<Organizations />} />
            <Route path="/orgs/:orgId" element={<OrgDetail />} />
            <Route path="/platform-users" element={<PlatformUsers />} />
            <Route path="/platform-health" element={<PlatformHealth />} />
            <Route path="/releases" element={<Releases />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthGate>
    </AuthProvider>
  );
}
